import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box, Button, Container, CssBaseline, TextField, ThemeProvider, InputAdornment, Tooltip,
} from '@mui/material';
import {
    DataGrid, GridActionsCellItem, Toolbar, QuickFilter, QuickFilterControl, QuickFilterClear,
} from '@mui/x-data-grid';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useSelector } from 'react-redux';

import theme, { TEXT_COLOR } from '../../theme';
import Topbar from '../Topbar';
import Card from '../Dashboard/Card';
import ResultsHeader from './ResultsHeader';
import FiltersPopover from './FiltersPopover';
import { LevelChip, NameCell, PerformancePill } from './cells';
import { resultsTokens as t } from './tokens';
import { withLanguage, performanceText } from '../common/Texts';
import { getText } from '../common/MultiLanguageText';
import { API } from '../../api';
import { downloadCsv, buildDownloadName } from '../../utils/downloadCsv';
import { downloadXlsx } from '../../utils/downloadXlsx';
import { buildReplyDownloadRows } from '../../utils/replyDownloadRows';
import { isHighPotential } from '../../utils/potential';
import { aiLevelText, aiLevelTooltip } from '../../utils/aiLevel';
import { alternativeLabelsText } from '../../utils/alternativeLabels';
import { toTitleCase } from '../../utils/toTitleCase';
import DownloadSplitButton from '../DownloadSplitButton';
import PerformanceImportDialog from './PerformanceImportDialog';

const EMPTY_FILTERS = { bu: [], dept: [], pos: [] };

function lookupMap(items, language) {
    const map = {};
    if (!Array.isArray(items)) return map;
    for (const item of items) {
        map[item.id] = getText(item, 'name', language);
    }
    return map;
}

function getLevel(row, calcField, sublevelField, tr) {
    if (row.invalid) return tr('invalid');
    if (!row[calcField]) return tr('in_progress');
    return `${row[calcField]}${row[sublevelField] || ''}`;
}

export default function Results() {
    const company = useSelector((s) => s.user.company);
    const language = useSelector((s) => s.user.language);
    const tr = useMemo(() => withLanguage(language), [language]);

    const [rows, setRows] = useState([]);
    const [isStaff, setIsStaff] = useState(false);
    const [loading, setLoading] = useState(false);
    const [filtersAnchor, setFiltersAnchor] = useState(null);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [importDialogOpen, setImportDialogOpen] = useState(false);

    const businessUnitMap = useMemo(() => lookupMap(company?.business_units, language), [company, language]);
    const positionMap = useMemo(() => lookupMap(company?.positions, language), [company, language]);
    const departmentMap = useMemo(() => lookupMap(company?.departments, language), [company, language]);

    const optionList = useCallback((items) => {
        if (!Array.isArray(items)) return [];
        return items
            .map((it) => ({ id: it.id, name: getText(it, 'name', language) }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [language]);

    const businessUnitOptions = useMemo(() => optionList(company?.business_units), [company, optionList]);
    const positionOptions = useMemo(() => optionList(company?.positions), [company, optionList]);
    const departmentOptions = useMemo(() => optionList(company?.departments), [company, optionList]);

    const fetchedFor = useRef(null);
    useEffect(() => {
        if (!company?.id || fetchedFor.current === company.id) return;
        const targetCompanyId = company.id;
        const controller = new AbortController();
        setLoading(true);
        Promise.all([
            API.get('/reply/', { signal: controller.signal }),
            API.get('/talentuser/', { signal: controller.signal }),
        ])
            .then(([reps, tu]) => {
                // Mark fetched only on success, so an aborted run (e.g. React
                // StrictMode's double-mount) does not block the retry.
                fetchedFor.current = targetCompanyId;
                setRows(reps.data);
                setIsStaff(Boolean(tu.data?.user?.is_staff));
            })
            .catch((e) => {
                if (e?.name === 'AbortError') return;
                console.error('Error en Api:', e);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
    }, [company?.id]);

    const reloadReplies = useCallback(() => {
        API.get('/reply/')
            .then((reps) => setRows(reps.data))
            .catch((e) => console.error('Error en Api:', e));
    }, []);

    const ruleset = company?.potential_ruleset;

    const enrichedRows = useMemo(() => rows.map((r) => ({
        ...r,
        performanceLabel: performanceText(r.performance, tr),
        altoPot: isHighPotential(r, ruleset, language),
    })), [rows, tr, ruleset, language]);

    const filteredRows = useMemo(() => {
        const { bu, dept, pos } = filters;
        if (!bu.length && !dept.length && !pos.length) return enrichedRows;
        return enrichedRows.filter((r) => (
            (!bu.length || bu.includes(r.business_unit))
            && (!dept.length || dept.includes(r.department))
            && (!pos.length || pos.includes(r.position))
        ));
    }, [enrichedRows, filters]);

    const activeFilterCount = (
        (filters.bu.length ? 1 : 0)
        + (filters.dept.length ? 1 : 0)
        + (filters.pos.length ? 1 : 0)
    );

    const handleExport = useCallback((format = 'csv') => {
        // Se agrega la columna de desempeño con el valor actual de cada uno
        // para que el mismo archivo se pueda editar y volver a subir en "Carga
        // desempeño" (ver ReplyPerformanceBulk en el backend): las filas que
        // queden vacías se ignoran, así que sólo cambia lo que se completó.
        const data = buildReplyDownloadRows(filteredRows, {
            company, language, tr, includePerformance: true, includeAiLevel: isStaff,
            includeAlternatives: isStaff,
        });
        const fileName = buildDownloadName([company?.name, tr('results')], format);
        if (format === 'xlsx') {
            downloadXlsx(data, fileName);
        } else {
            downloadCsv(data, fileName);
        }
    }, [filteredRows, company, language, tr, isStaff]);

    const columns = useMemo(() => {
        const cols = [
            {
                field: 'id', headerName: tr('id'), width: 72,
            },
            {
                field: 'name', headerName: tr('name'), flex: 1.6, minWidth: 200,
                valueGetter: (value) => toTitleCase(value),
                renderCell: (params) => (
                    <NameCell
                        name={params.value}
                        highlight={Boolean(params.row.altoPot)}
                    />
                ),
            },
            { field: 'age', headerName: tr('age'), width: 60 },
            {
                field: 'business_unit', headerName: tr('business_unit'), flex: 1,
                valueGetter: (value) => toTitleCase(businessUnitMap[value] ?? ''),
            },
            {
                field: 'position', headerName: tr('position'), flex: 1,
                valueGetter: (value) => toTitleCase(positionMap[value] ?? ''),
            },
            {
                field: 'department', headerName: tr('area'), flex: 1,
                valueGetter: (value) => toTitleCase(departmentMap[value] ?? ''),
            },
        ];

        if (isStaff) {
            cols.push({
                field: 'level_ai', headerName: tr('level_ai'), flex: 0.6, sortable: false,
                valueGetter: (_v, row) => aiLevelText(row.ai_classification),
                renderCell: (params) => (
                    <LevelChip
                        value={params.value}
                        title={aiLevelTooltip(params.row.ai_classification, tr)}
                    />
                ),
            });
        }

        cols.push({
            field: 'level_label', headerName: isStaff ? tr('label') : tr('level'), flex: 0.6,
            valueGetter: (_v, row) => getLevel(row, 'level_label', 'sublevel_label', tr),
            renderCell: (params) => <LevelChip value={params.value}/>,
        });

        if (isStaff) {
            cols.push({
                field: 'alternative_labels', headerName: tr('alternative_labels_short'), flex: 0.8,
                sortable: false,
                valueGetter: (_v, row) => alternativeLabelsText(row.alternative_labels),
            });
        }

        cols.push({
            field: 'performanceLabel', headerName: tr('performance'), width: 130,
            renderCell: (params) => (
                <PerformancePill rawValue={params.row.performance} label={params.value}/>
            ),
        });

        if (isStaff) {
            cols.push({
                field: 'state', headerName: tr('state'), flex: 0.8, sortable: false,
                valueGetter: (_v, row) => (row.status === 'S' ? tr('not_answered') : tr('answered')),
            });
        }

        cols.push({
            field: 'actions', type: 'actions', headerName: '', width: 96, sortable: false,
            getActions: (params) => [
                <Tooltip key="label" title={tr('edit')}>
                    <GridActionsCellItem
                        icon={<EditOutlinedIcon fontSize="small" sx={{ color: TEXT_COLOR }}/>}
                        label={tr('edit')}
                        onClick={() => window.open(`/#/label/${params.row.id}`, '_blank', 'noopener,noreferrer')}
                    />
                </Tooltip>,
                <Tooltip key="report" title={tr('view_report')}>
                    <GridActionsCellItem
                        icon={<DescriptionOutlinedIcon fontSize="small" sx={{ color: TEXT_COLOR }}/>}
                        label={tr('view_report')}
                        onClick={() => window.open(`/#/report/c/${params.row.id}`, '_blank', 'noopener,noreferrer')}
                    />
                </Tooltip>,
            ],
        });

        return cols;
    }, [tr, isStaff, businessUnitMap, positionMap, departmentMap]);

    const tableToolbar = () => (
        <Toolbar
            sx={{
                px: 2, pt: 5, pb: 5, gap: 1.5,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderBottom: `1px solid ${t.color.divider}`,
            }}
        >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                    size="small"
                    onClick={(e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        setFiltersAnchor({ top: r.top, left: r.right + 8 });
                    }}
                    startIcon={<FilterListIcon fontSize="small"/>}
                    sx={{
                        color: t.color.fg,
                        bgcolor: 'transparent',
                        border: `1px solid ${t.color.border}`,
                        borderRadius: `${t.radius.md}px`,
                        px: 1.5,
                        '&:hover': { bgcolor: t.color.bgSub },
                    }}
                >
                    {tr('filters')}
                    {activeFilterCount > 0 && (
                        <Box component="span" sx={{
                            ml: 1, px: 0.875, py: 0.125,
                            borderRadius: 999,
                            backgroundColor: t.color.primarySoft,
                            color: t.color.primary,
                            fontSize: 11, fontWeight: 700,
                        }}>{activeFilterCount}</Box>
                    )}
                </Button>
                <DownloadSplitButton
                    onDownload={handleExport}
                    label={tr('download_csv')}
                    csvLabel={tr('download_as_csv')}
                    xlsxLabel={tr('download_as_xlsx')}
                    buttonSx={{
                        color: t.color.fg,
                        borderColor: t.color.border,
                        backgroundColor: t.color.surface,
                        '&:hover': {
                            borderColor: t.color.primary,
                            color: t.color.primary,
                            backgroundColor: t.color.primarySoft,
                        },
                    }}
                    arrowSx={{
                        borderColor: t.color.border,
                        backgroundColor: t.color.surface,
                        '&:hover': {
                            borderColor: t.color.primary,
                            backgroundColor: t.color.primarySoft,
                        },
                    }}
                />
                <Button
                    size="small"
                    startIcon={<UploadFileIcon fontSize="small"/>}
                    onClick={() => setImportDialogOpen(true)}
                    sx={{
                        color: t.color.fg,
                        bgcolor: 'transparent',
                        border: `1px solid ${t.color.border}`,
                        borderRadius: `${t.radius.md}px`,
                        px: 1.5,
                        '&:hover': { bgcolor: t.color.bgSub },
                    }}
                >
                    {tr('bulk_performance_upload')}
                </Button>
            </Box>

            <QuickFilter>
                <QuickFilterControl
                    render={({ ref, ...other }) => (
                        <TextField
                            {...other}
                            inputRef={ref}
                            size="small"
                            placeholder={tr('search_placeholder')}
                            variant="outlined"
                            sx={{
                                width: 320,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: `${t.radius.md}px`,
                                    bgcolor: t.color.surface,
                                    '& fieldset': { borderColor: t.color.border },
                                },
                            }}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" sx={{ color: t.color.fgMuted }}/>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    )}
                />
                <QuickFilterClear sx={{ ml: 0.5, color: t.color.fgMuted }}/>
            </QuickFilter>
        </Toolbar>
    );

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline/>
            <Box sx={{ minHeight: '100vh', bgcolor: t.color.bgSub }}>
                <Topbar/>
                <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
                    <ResultsHeader
                        language={language}
                        count={filteredRows.length}
                    />

                    <Card padded={false} sx={{ overflow: 'hidden' }}>
                        <DataGrid
                            rows={filteredRows}
                            columns={columns}
                            loading={loading}
                            autoHeight
                            disableColumnMenu
                            disableRowSelectionOnClick
                            pageSizeOptions={[10, 25, 50, 100]}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 10 } },
                                sorting: { sortModel: [{ field: 'id', sort: 'desc' }] },
                            }}
                            showToolbar
                            slots={{ toolbar: tableToolbar }}
                            localeText={{
                                MuiTablePagination: {
                                    labelRowsPerPage: tr('rows_per_page'),
                                },
                            }}
                            sx={{
                                border: 'none',
                                fontFamily: t.font.sans,
                                '& .MuiDataGrid-columnHeaders': {
                                    borderBottom: `1px solid ${t.color.divider}`,
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    backgroundColor: t.color.bgSub,
                                },
                                '& .MuiDataGrid-columnSeparator': {
                                    display: 'none',
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: TEXT_COLOR,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.6,
                                    whiteSpace: 'normal',
                                    lineHeight: 1.3,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                },
                                '& .MuiDataGrid-columnHeaderTitleContainer': {
                                    overflow: 'visible',
                                },
                                '& .MuiDataGrid-cell': {
                                    fontSize: 13,
                                    color: t.color.fg,
                                    borderBottom: `1px solid ${t.color.divider}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                                '& .MuiDataGrid-row:hover': {
                                    backgroundColor: t.color.bgSub,
                                },
                                '& .MuiDataGrid-footerContainer': {
                                    borderTop: `1px solid ${t.color.divider}`,
                                },
                                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                                    outline: 'none',
                                },
                                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                                    outline: 'none',
                                },
                            }}
                        />
                    </Card>
                </Container>
            </Box>

            <FiltersPopover
                anchorPosition={filtersAnchor}
                onClose={() => setFiltersAnchor(null)}
                language={language}
                businessUnits={businessUnitOptions}
                positions={positionOptions}
                departments={departmentOptions}
                selected={filters}
                onChange={setFilters}
                onClear={() => setFilters(EMPTY_FILTERS)}
            />

            <PerformanceImportDialog
                open={importDialogOpen}
                onClose={() => setImportDialogOpen(false)}
                onImported={reloadReplies}
                tr={tr}
            />
        </ThemeProvider>
    );
}
